<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import Preview from "../../../base-components/Preview";
import Table from "../../../base-components/Table";
import Button from "../../../base-components/Button/Button.vue";
import { FormLabel } from "../../../base-components/Form";
import Litepicker from "../../../base-components/Litepicker";
import TomSelect from "../../../custom-components/TomSelect/TomSelect.vue";
import { useRegistrosTurnoStore } from "../../../services/registrosTurno";
import { useUsuariosStore } from "../../../services/usuarios";
import { userUserStore } from "../../../stores/user";
import type { RegistroTurno } from "@fixeduy/restaurantestypes";
import { getTipoRegistroTurnoLabel } from "../../../constants/tiposRegistroTurno";
import { TIPOS_REGISTRO_TURNO } from "../../../constants/tiposRegistroTurno";
import { getCurrentDate } from "../../../utils/fecha";
import {
  getFirstDayOfMonth,
  litePickerConfig,
  stringToDate
} from "../../../utils/helper";
import ExcelJS from "exceljs";

const userStore = userUserStore();
const usuariosStore = useUsuariosStore();
const registros = ref<RegistroTurno[]>([]);
const cargando = ref(false);

const fechaInicioFiltro = ref(getFirstDayOfMonth());
const fechaFinFiltro = ref(getCurrentDate());
const usuarioIdFiltro = ref("");

const fechaInicioTs = computed(() => {
  const str = fechaInicioFiltro.value || getFirstDayOfMonth();
  return stringToDate(str).getTime();
});

const fechaFinTs = computed(() => {
  const str = fechaFinFiltro.value || getCurrentDate();
  const endOfDay = stringToDate(str).getTime() + 24 * 60 * 60 * 1000 - 1;
  return endOfDay;
});

const opcionesUsuarios = computed(() => {
  const list = (usuariosStore.usuarios ?? []).map(u => ({
    value: u.id,
    label: u.nombre
  }));
  return [{ value: "", label: "Todos" }, ...list];
});

/** Fecha hasta debe ser mayor o igual a fecha desde. */
const rangoFechasValido = computed(() => {
  const strDesde = fechaInicioFiltro.value || getFirstDayOfMonth();
  const strHasta = fechaFinFiltro.value || getCurrentDate();
  const inicioDesde = stringToDate(strDesde).getTime();
  const inicioHasta = stringToDate(strHasta).getTime();
  return inicioHasta >= inicioDesde;
});

const cargarRegistros = async () => {
  if (!userStore.empresa?.id) return;
  if (!rangoFechasValido.value) return;
  cargando.value = true;
  try {
    registros.value = await useRegistrosTurnoStore.getRegistrosPorRango(
      fechaInicioTs.value,
      fechaFinTs.value,
      usuarioIdFiltro.value || undefined
    );
  } finally {
    cargando.value = false;
  }
};

const formatearTimestamp = (ts: number): string => {
  const d = new Date(ts * 1000);
  const dia = String(d.getDate()).padStart(2, "0");
  const mes = String(d.getMonth() + 1).padStart(2, "0");
  const año = d.getFullYear();
  const h = String(d.getHours()).padStart(2, "0");
  const m = String(d.getMinutes()).padStart(2, "0");
  return `${dia}/${mes}/${año} ${h}:${m}`;
};

const badgeClasses = (tipo: string): string => {
  const opt = TIPOS_REGISTRO_TURNO.find(t => t.value === tipo);
  return opt?.badgeClasses ?? "";
};

onMounted(() => {
  usuariosStore.getFromFirestore();
  cargarRegistros();
});

const handleExport = async () => {
  const workbook = new ExcelJS.Workbook();

  const worksheet = workbook.addWorksheet("Hoja 1");
  const encabezados = ["Empleado", "Tipo", "Hora"];
  worksheet.addRow(encabezados);

  for (const registro of registros.value) {
    const datos = [
      registro.usuarioNombre,
      getTipoRegistroTurnoLabel(registro.tipo),
      formatearTimestamp(registro.timestamp)
    ];
    worksheet.addRow(datos);
  }

  workbook.xlsx.writeBuffer().then(buffer => {
    const blob = new Blob([buffer], { type: "application/octet-stream" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `Turnos - ${fechaInicioFiltro.value}- ${fechaFinFiltro.value}.xlsx`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  });
};
</script>

<template>
  <Preview class="box mt-4">
    <div class="px-4 pt-3 pb-2">
      <div class="flex flex-wrap items-end gap-4">
        <div>
          <FormLabel
            htmlFor="fecha-inicio-turnos"
            class="block text-xs font-medium text-slate-500 mb-1"
          >
            Fecha desde
          </FormLabel>
          <Litepicker
            id="fecha-inicio-turnos"
            v-model="fechaInicioFiltro"
            :options="litePickerConfig"
            class="min-w-28"
          />
        </div>
        <div>
          <FormLabel
            htmlFor="fecha-fin-turnos"
            class="block text-xs font-medium text-slate-500 mb-1"
          >
            Fecha hasta
          </FormLabel>
          <Litepicker
            id="fecha-fin-turnos"
            v-model="fechaFinFiltro"
            :options="litePickerConfig"
            class="min-w-28"
            :class="{ 'border-danger': !rangoFechasValido }"
          />
          <p v-if="!rangoFechasValido" class="mt-1 text-xs text-danger">
            La fecha hasta debe ser mayor o igual a la fecha desde.
          </p>
        </div>
        <div>
          <FormLabel
            htmlFor="usuario-turnos"
            class="block text-xs font-medium text-slate-500 mb-1"
          >
            Usuario
          </FormLabel>
          <TomSelect
            id="usuario-turnos"
            v-model="usuarioIdFiltro"
            class="min-w-[180px]"
            :options="{
              placeholder: 'Todos'
            }"
          >
            <option
              v-for="opt in opcionesUsuarios"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </TomSelect>
        </div>
        <Button
          variant="secondary"
          :disabled="cargando || !rangoFechasValido"
          @click="cargarRegistros"
        >
          Actualizar
        </Button>
        <Button variant="secondary" @click="handleExport">
          Exportar a Excel
        </Button>
      </div>
    </div>

    <div class="px-4 pb-4">
      <Preview.Panel>
        <Table hover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th class="whitespace-nowrap">Empleado</Table.Th>
              <Table.Th class="whitespace-nowrap">Tipo</Table.Th>
              <Table.Th class="whitespace-nowrap">Hora</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            <Table.Tr v-for="r in registros" :key="r.id">
              <Table.Td>{{ r.usuarioNombre }}</Table.Td>
              <Table.Td>
                <span
                  class="inline-flex px-2 py-1 text-xs font-medium rounded"
                  :class="badgeClasses(r.tipo)"
                >
                  {{ getTipoRegistroTurnoLabel(r.tipo) }}
                </span>
              </Table.Td>
              <Table.Td>{{ formatearTimestamp(r.timestamp) }}</Table.Td>
            </Table.Tr>
            <Table.Tr v-if="cargando">
              <Table.Td colspan="3" class="text-center py-6 text-slate-500">
                Cargando...
              </Table.Td>
            </Table.Tr>
            <Table.Tr v-else-if="!cargando && registros.length === 0">
              <Table.Td colspan="3" class="text-center py-8 text-slate-500">
                No hay registros para esta fecha.
              </Table.Td>
            </Table.Tr>
          </Table.Tbody>
        </Table>
      </Preview.Panel>
    </div>
  </Preview>
</template>
